import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** Aborts any in-flight product fetches when the card leaves the DOM. */
  #abortController = new AbortController();
  /** The URL whose load currently owns the modal, if any. @type {string | null} */
  #pendingUrl = null;
  /** In-flight requests, keyed by URL, so hover prefetch and tap share one. @type {Map<string, Promise<Element | null>>} */
  #inFlight = new Map();
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    // if (url.searchParams.has('variant')) {
    //   return url.toString();
    // }

    // const selectedVariantId = this.#getSelectedVariantId();
    // if (selectedVariantId) {
    //   url.searchParams.set('variant', selectedVariantId);
    // }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Cards are disconnected and reconnected by morphs, so start each connection with a live
    // controller — otherwise every fetch after the first reconnect aborts immediately.
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController.abort();
    this.#inFlight.clear();
    this.#pendingUrl = null;
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    // Single-option products, and any response we failed to parse a picker out of, have none.
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Replaces the modal content with the loading skeleton, so a slow first open never shows an
   * empty sheet — and never briefly shows the previously opened product.
   */
  #showSkeleton() {
    const modalContent = document.getElementById('quick-add-modal-content');
    const skeleton = /** @type {HTMLTemplateElement | null} */ (
      document.getElementById('quick-add-modal-skeleton')
    );

    if (!modalContent || !skeleton) return;

    modalContent.setAttribute('aria-busy', 'true');
    modalContent.replaceChildren(skeleton.content.cloneNode(true));
  }

  /**
   * Renders cached product grid content into the modal.
   * @param {Element} cachedGrid - The cached (unmutated) product grid element
   */
  #renderContent(cachedGrid) {
    // Work on a clone — updateQuickAddModal mutates its argument on mobile.
    this.updateQuickAddModal(/** @type {Element} */ (cachedGrid.cloneNode(true)));

    // A second, pristine clone. The first one is restructured on mobile and morph adopts nodes
    // out of it into the live document, so it is no longer a safe morph source.
    this.#updateVariantPicker(/** @type {Element} */ (cachedGrid.cloneNode(true)));
  }

  /**
   * Handles quick add button click.
   *
   * The modal is opened synchronously, before any network work, so the first tap always
   * produces immediate feedback. The content is filled in when the fetch resolves.
   * @param {Event} event - The click event
   */
  handleClick = (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    const cachedGrid = this.#cachedContent.get(currentUrl);

    if (cachedGrid) {
      // Repeat open: fill first so the sheet animates in already populated.
      this.#renderContent(cachedGrid);
      this.#openQuickAddModal();
      return;
    }

    // Only start a load if one for this product isn't already running. A second tap must not
    // abort the first request and re-open — that is what used to surface an empty or stale
    // module. It still (re-)opens the sheet below, in case the first one was dismissed.
    if (this.#pendingUrl !== currentUrl) {
      this.#showSkeleton();
      this.loadProduct(currentUrl);
    }

    this.#openQuickAddModal();
  };

  /**
   * Resolves this component's grid content for a URL, from cache where possible.
   *
   * Requests are de-duplicated per URL, so the hover prefetch and a tap that lands mid-prefetch
   * share one request instead of cancelling each other.
   * @param {string} productPageUrl - Absolute product page URL
   * @returns {Promise<Element | null>} The cached (unmutated) grid element
   */
  #getProductGrid(productPageUrl) {
    const cached = this.#cachedContent.get(productPageUrl);
    if (cached) return Promise.resolve(cached);

    const inFlight = this.#inFlight.get(productPageUrl);
    if (inFlight) return inFlight;

    const request = this.fetchProductPage(productPageUrl)
      .then((html) => {
        const gridElement = html?.querySelector('[data-product-grid-content]');
        if (!gridElement) return null;

        // Cache the cloned element to avoid modifying the original
        const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#cachedContent.set(productPageUrl, productGrid);

        return productGrid;
      })
      .finally(() => this.#inFlight.delete(productPageUrl));

    this.#inFlight.set(productPageUrl, request);

    return request;
  }

  /**
   * Loads a product page into the modal.
   * Also used by the variant picker to swap the sheet to a combined-listing sibling product.
   * @param {string} url - The URL of the product page to load
   */
  async loadProduct(url) {
    if (!url) return;

    // Normalise so a relative sibling-product URL and this card's absolute one share a cache key.
    const productPageUrl = new URL(url, window.location.origin).toString();

    if (this.#pendingUrl === productPageUrl) return;

    this.#pendingUrl = productPageUrl;

    try {
      const productGrid = await this.#getProductGrid(productPageUrl);

      // A newer load took over while this one was in flight — let it own the modal.
      if (this.#pendingUrl !== productPageUrl) return;

      // The modal is shared by every card. If the shopper has since dismissed this sheet or
      // opened another product's, this result is stale — cache it, but do not paint it.
      if (!this.hasAttribute('stay-visible')) return;

      if (productGrid) {
        this.#renderContent(productGrid);
      } else {
        this.#showLoadError(productPageUrl);
      }
    } catch (error) {
      console.error(error);
      if (this.#pendingUrl === productPageUrl && this.hasAttribute('stay-visible')) this.#showLoadError(productPageUrl);
    } finally {
      if (this.#pendingUrl === productPageUrl) this.#pendingUrl = null;
    }
  }

  /**
   * Replaces the skeleton with a message and a link out to the product page.
   * @param {string} [productPageUrl] - The URL that failed to load. Defaults to this card's
   *   product; pass it explicitly so a failed combined-listing sibling links to itself.
   */
  #showLoadError(productPageUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const template = /** @type {HTMLTemplateElement | null} */ (document.getElementById('quick-add-modal-error'));

    if (!modalContent || !template) return;

    const content = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const link = content.querySelector('a');

    if (link) link.href = productPageUrl || this.productPageUrl;

    modalContent.removeAttribute('aria-busy');
    modalContent.replaceChildren(content);
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    // On mobile the sheet body is the only scroller; the two below only exist on desktop.
    const modalContent = dialogComponent.querySelector('.quick-add-modal__content');
    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    modalContent?.scrollTo({ top: 0, behavior: 'instant' });
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    // Record which card owns the sheet, so the variant picker inside it can load a
    // combined-listing sibling product back into the right component.
    dialogComponent.opener = this;

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Warms the content cache for a product page, without touching the modal.
   *
   * Called by product-card on hover/focus and after a variant update. Requests are
   * de-duplicated per URL, so this can never cancel a load a tap is waiting on.
   * @param {string} url - The URL of the product page to prefetch
   * @returns {Promise<Element | null>}
   */
  async prefetchProduct(url) {
    if (!url) return null;

    try {
      return await this.#getProductGrid(new URL(url, window.location.origin).toString());
    } catch (error) {
      // A prefetch failing is not worth surfacing — the tap will retry and report.
      return null;
    }
  }

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    const response = await fetch(productPageUrl, {
      signal: this.#abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    const responseText = await response.text();

    return new DOMParser().parseFromString(responseText, 'text/html');
  }

  /**
   * Floats the gallery's dot controls on top of the image for the mobile sheet.
   *
   * The gallery renders its mobile controls with `controls_on_media: false`
   * (blocks/_product-media-gallery.liquid), so they occupy their own static row below the
   * image — 44px of height that pushed the dots into the text underneath. Setting the
   * attribute here opts into the theme's own on-media treatment from base.css, which
   * absolutely positions them, raises the z-index and switches the dots to white for
   * contrast over a photo. Done on the fetched clone rather than in the shared snippet so
   * the product page keeps its current layout.
   * @param {Element} productGrid - The product grid element
   */
  #floatGalleryControlsOnMedia(productGrid) {
    const mobileControls = productGrid.querySelector('.media-gallery__mobile-controls');
    if (!mobileControls) return;

    mobileControls.setAttribute('controls-on-media', '');
    mobileControls.setAttribute('pagination-position', 'center');
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Every width, not just mobile: on desktop `.product-information__media` scrolls with hidden
    // scrollbars, so a dot row left in the flow turns it into a scrollbar-less nested scroller.
    this.#floatGalleryControlsOnMedia(productGrid);

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');

      // Read the title out of the fetched document rather than this card's dataset — a
      // combined-listing colour swap reloads the sheet with a different product.
      const fetchedTitle = productGrid.querySelector('.view-product-title a');
      const productTitle = document.createElement('a');
      productTitle.textContent = fetchedTitle?.textContent?.trim() || this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = fetchedTitle?.getAttribute('href') || this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);
    modalContent.removeAttribute('aria-busy');

    this.#reserveFooterSpace(modalContent);
    this.#syncVariantSelection(modalContent);
  }

  /**
   * Reserves room at the bottom of the sheet for the fixed Add to Bag footer.
   *
   * The footer is taken out of flow on mobile so it can never cover the Size row, which means
   * the sheet has to reserve its height itself. That height is not fixed — apps inject into the
   * buy-buttons area (Giftnote's "add a gift message" button roughly doubles it) and it changes
   * again when a combined-listing colour swap loads a different product — so measure it per
   * render rather than hardcoding a guess.
   * @param {Element} modalContent - The morphed modal content element
   */
  #reserveFooterSpace(modalContent) {
    if (!isMobileBreakpoint() || !(modalContent instanceof HTMLElement)) return;

    const form = modalContent.querySelector('product-form-component');

    if (!(form instanceof HTMLElement) || !form.offsetHeight) {
      modalContent.style.removeProperty('--quick-add-footer-height');
      return;
    }

    modalContent.style.setProperty('--quick-add-footer-height', `${form.offsetHeight}px`);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    // const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    // for (const input of modalInputs) {
    //   if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
    //     input.checked = true;
    //     input.dispatchEvent(new Event('change', { bubbles: true }));
    //     break;
    //   }
    // }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /**
   * The quick-add component that opened this sheet, set by QuickAddComponent on open.
   * @type {QuickAddComponent | null}
   */
  opener = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    this.opener = null;

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
